import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';

import { Jwt } from '@pkg/classes';

import { apiFactory } from '@app/client';
import { Admin } from '@app/client/resources';

import { type RootState } from '../reducers';
import {
    ACTION_SET_ADMIN_INFO,
    ACTION_SET_ADMIN_INFO_ENABLE_INTERVAL,
    type AdminInfo,
    type AdminInfoActionTypes,
} from '../types/admin-info';

let checkInterval;

const setAdminInfo = (information: AdminInfo): AdminInfoActionTypes => {
    return {
        type: ACTION_SET_ADMIN_INFO,
        information,
    };
};

export const setEnableIntervalCheck = (enableIntervalCheck: boolean): AdminInfoActionTypes => ({
    type: ACTION_SET_ADMIN_INFO_ENABLE_INTERVAL,
    enableIntervalCheck,
});

const screenSizes = getWindowSizes();
const expiry = getTokenExpiry();

const zoom = checkZoom();
const { screenSize, viewSize } = screenSizes;

const { accessTokenExpiresAt, refreshTokenExpiresAt, twoFactorExpiresAt } = expiry;

export function initializeAdminInfo() {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        const status = await apiFactory().call(Admin.Auth.getStatus, {});
        const apiVersion = status.headers.get('x-app-version') || 'Error';
        const userAccount = getState().user?.userAccount;
        const enableIntervalCheck = getState().adminInfo.enableIntervalCheck;

        let role, email, twoFactorAuthTypes;
        if (userAccount) {
            role = userAccount.role;
            email = userAccount.user.email;
            twoFactorAuthTypes = userAccount.user.two_factor_auth;
        }

        dispatch(
            setAdminInfo({
                host: window.location.host,
                webVersion: window['yatVersion'],
                apiVersion,
                role,
                email,
                twoFactorAuthTypes,
                zoom,
                screenSize,
                viewSize,
                accessTokenExpiresAt,
                refreshTokenExpiresAt,
                twoFactorExpiresAt,
            }),
        );
        if (enableIntervalCheck) {
            dispatch(intervalCheck());
        }
    };
}

function checkZoom() {
    return Math.round(window.devicePixelRatio * 100);
}
function getWindowSizes() {
    const { availHeight, availWidth } = window.screen;
    const { innerHeight, innerWidth } = window;
    return { screenSize: `${availWidth} X ${availHeight}`, viewSize: `${innerWidth} X ${innerHeight}` };
}
function getTokenExpiry() {
    let twoFactorExpiresAt, accessTokenExpiresAt, refreshTokenExpiresAt;
    try {
        const tokenState = JSON.parse(localStorage.getItem('tokens') || '{}');
        const { access_token, refresh_token } = tokenState.state.token || {};
        const accessJwt = new Jwt(access_token);
        const refreshJwt = new Jwt(refresh_token);
        accessTokenExpiresAt = accessJwt.jwt?.exp;
        twoFactorExpiresAt = accessJwt.jwt?.active2fa;
        refreshTokenExpiresAt = refreshJwt.jwt?.exp;
    } catch {
        // Noop
    }
    return {
        twoFactorExpiresAt,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
    };
}
export function intervalCheck() {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        clearInterval(checkInterval);
        checkInterval = setInterval(() => {
            dispatch(
                setAdminInfo({
                    zoom,
                    screenSize,
                    viewSize,
                    accessTokenExpiresAt,
                    refreshTokenExpiresAt,
                    twoFactorExpiresAt,
                }),
            );
        }, 5000);
    };
}

export function stopInterval() {
    clearInterval(checkInterval);
}
